export interface VisitorChecklistAnswers {
  visitorMaritalStatus?: 'single' | 'married' | 'common-law' | 'divorced' | 'widowed' | 'separated';
  visitorSpouseAccompanying?: 'oui' | 'non';
  visitorHasCanadaInvitation?: 'oui' | 'non';
  visitorInviterRelationship?: string;
  visitorInviterRelationshipOther?: string;
  visitorInviterStatus?: string;
  visitorInviterStatusOther?: string;
  visitorInviterHosts?: 'oui' | 'non';
  visitorInviterPays?: 'oui' | 'non';
}

export const VISITOR_RELATIONSHIPS = [
  { value: 'spouse', label: 'Époux / épouse ou conjoint de fait' },
  { value: 'parent', label: 'Père / mère' },
  { value: 'child', label: 'Fils / fille' },
  { value: 'sibling', label: 'Frère / sœur' },
  { value: 'grandparent', label: 'Grand-père / grand-mère' },
  { value: 'grandchild', label: 'Petit-fils / petite-fille' },
  { value: 'uncle-aunt', label: 'Oncle / tante' },
  { value: 'cousin', label: 'Cousin / cousine' },
  { value: 'friend', label: 'Ami / amie' },
  { value: 'other', label: 'Autre lien' }
];

export const VISITOR_INVITER_STATUSES = [
  { value: 'citizen', label: 'Citoyen canadien' },
  { value: 'permanent-resident', label: 'Résident permanent' },
  { value: 'worker', label: 'Travailleur temporaire' },
  { value: 'student', label: 'Étudiant étranger' },
  { value: 'visitor', label: 'Visiteur au Canada' },
  { value: 'other', label: 'Autre statut — à préciser' }
];

export const visitorHasPartner = (answers: VisitorChecklistAnswers) =>
  ['married', 'common-law', 'separated'].includes(answers.visitorMaritalStatus || '');

export const isVisitorInvitationComplete = (answers: VisitorChecklistAnswers) => {
  if (answers.visitorHasCanadaInvitation === 'non') return true;
  return answers.visitorHasCanadaInvitation === 'oui'
    && VISITOR_RELATIONSHIPS.some(option => option.value === answers.visitorInviterRelationship)
    && VISITOR_INVITER_STATUSES.some(option => option.value === answers.visitorInviterStatus)
    && (answers.visitorInviterRelationship !== 'other' || Boolean(answers.visitorInviterRelationshipOther?.trim()))
    && (answers.visitorInviterStatus !== 'other' || Boolean(answers.visitorInviterStatusOther?.trim()))
    && ['oui', 'non'].includes(answers.visitorInviterHosts || '')
    && ['oui', 'non'].includes(answers.visitorInviterPays || '');
};

export const getVisitorFamilyDocuments = (answers: VisitorChecklistAnswers): string[] => {
  const docs: string[] = [];
  if (['married', 'separated'].includes(answers.visitorMaritalStatus || '')) {
    docs.push("• Copie de l'acte de mariage en langue d'origine (en arabe si l'acte est rédigé en arabe), même si l'époux/épouse ne voyage pas.");
    docs.push("• Traduction française de l'acte de mariage s'il n'est ni en français ni en anglais (traduction anglaise également acceptée), avec attestation ou affidavit du traducteur lorsque requis.");
  } else if (answers.visitorMaritalStatus === 'common-law') {
    docs.push("• Déclaration officielle d'union de fait IMM 5409 et preuves de vie commune : bail commun, factures ou autres justificatifs pertinents.");
  } else if (answers.visitorMaritalStatus === 'divorced') {
    docs.push("• Copie du jugement ou certificat de divorce et traduction en français ou en anglais si nécessaire.");
  } else if (answers.visitorMaritalStatus === 'widowed') {
    docs.push("• Copie de l'acte de décès de l'époux/épouse et traduction en français ou en anglais si nécessaire.");
  }
  return docs.length ? ['--- SITUATION FAMILIALE DU DEMANDEUR ---', ...docs] : [];
};

export const getVisitorInvitationDocuments = (answers: VisitorChecklistAnswers): string[] => {
  const accommodation = "• Justificatif de l'hébergement prévu au Canada : réservation d'hôtel annulable ou autre preuve adaptée au séjour, si disponible.";
  if (answers.visitorHasCanadaInvitation === 'non') return [accommodation];
  if (answers.visitorHasCanadaInvitation !== 'oui') return [];

  const docs = [
    '--- INVITATION AU CANADA ---',
    "• Lettre d'invitation datée et signée : identité et coordonnées de l'hôte et du visiteur, lien entre eux, motif et dates du séjour, hébergement prévu, répartition des dépenses et date de retour envisagée."
  ];
  const statusDocuments: Record<string, string> = {
    citizen: "• Preuve de citoyenneté canadienne de la personne qui invite : passeport canadien, certificat de citoyenneté ou acte de naissance canadien, selon sa situation.",
    'permanent-resident': "• Copie de la pièce d'identité de l'hôte et preuve de résidence permanente au Canada : carte de résident permanent ou autre document officiel approprié.",
    worker: "• Copie du passeport et du permis de travail valide de la personne qui invite, ou autre preuve de son statut légal actuel au Canada.",
    student: "• Copie du passeport et du permis d'études valide de la personne qui invite, ou autre preuve de son statut légal actuel au Canada.",
    visitor: "• Copie du passeport de l'hôte et preuve de son statut de visiteur actuel : fiche de visiteur ou preuve d'entrée et de séjour autorisé. Un visa seul ne prouve pas le statut actuel au Canada.",
    other: "• Copie de la pièce d'identité de l'hôte et document officiel établissant son statut légal actuel au Canada ; faire vérifier le document adapté au statut déclaré."
  };
  if (statusDocuments[answers.visitorInviterStatus || '']) docs.push(statusDocuments[answers.visitorInviterStatus!]);

  const relationship = VISITOR_RELATIONSHIPS.find(option => option.value === answers.visitorInviterRelationship)?.label;
  if (answers.visitorInviterRelationship === 'friend' || answers.visitorInviterRelationship === 'other') {
    docs.push(`• Éléments expliquant le lien avec la personne qui invite (${answers.visitorInviterRelationship === 'other' ? answers.visitorInviterRelationshipOther?.trim() : relationship}) : lettre explicative et, si disponibles, échanges ou autres preuves pertinentes.`);
  } else if (answers.visitorInviterRelationship === 'spouse') {
    docs.push("• Preuve du lien avec l'hôte : acte de mariage ou justificatifs d'union de fait, avec traduction en français ou en anglais si nécessaire.");
  } else {
    docs.push(`• Preuve du lien de parenté avec l'hôte (${relationship}) : actes de naissance et, selon le lien, actes de mariage ou livret de famille permettant de reconstituer la filiation ; traductions si nécessaires.`);
  }
  if (answers.visitorInviterHosts === 'oui') {
    docs.push("• Confirmation d'hébergement chez l'hôte avec l'adresse et la durée du séjour ; justificatif de son adresse au Canada (bail, titre de propriété ou facture récente), selon le dossier.");
  } else {
    docs.push(accommodation);
  }
  if (answers.visitorInviterPays === 'oui') {
    docs.push("• Lettre de prise en charge de l'hôte précisant les dépenses couvertes et la durée du soutien financier.");
    docs.push("• Justificatifs financiers de l'hôte pour la prise en charge : relevés bancaires récents et preuves de revenus adaptées à sa situation (emploi, fiches de paie, avis de cotisation ou autres ressources).");
  }
  return docs;
};
